import {
  ANGEL_GUILD_BASIC_MONTHLY_OFFER_ID,
  ANGEL_GUILD_BASIC_NO_ADS_MONTHLY_20_OFFER_ID,
  ANGEL_GUILD_BASIC_NO_ADS_MONTHLY_OFFER_ID,
  ANGEL_GUILD_BASIC_NO_ADS_YEARLY_179_OFFER_ID,
  ANGEL_GUILD_BASIC_NO_ADS_YEARLY_OFFER_ID,
  ANGEL_GUILD_BASIC_YEARLY_OFFER_ID,
  ANGEL_GUILD_PREMIUM_MONTHLY_22_OFFER_ID,
  ANGEL_GUILD_PREMIUM_MONTHLY_25_OFFER_ID,
  ANGEL_GUILD_PREMIUM_MONTHLY_OFFER_ID,
  ANGEL_GUILD_PREMIUM_YEARLY_189_OFFER_ID,
  ANGEL_GUILD_PREMIUM_YEARLY_199_OFFER_ID,
  ANGEL_GUILD_PREMIUM_YEARLY_OFFER_ID,
} from '@/constants/offer-ids'

export const GUILD_PREMIUM_MONTHLY_STANDARD_PRICE = 2000

export const GUILD_BASIC_MONTHLY_STANDARD_PRICE = 1200

export const GUILD_BASIC_NO_ADS_MONTHLY_STANDARD_PRICE = 1800

/** The price the user would pay if they signed up monthly at full price over 12 months **/
export const GUILD_PREMIUM_YEARLY_FULL_PRICE = 24000

/** The standard discounted pricing for Premium Yearly **/
export const GUILD_PREMIUM_YEARLY_STANDARD_PRICE = 17900

/** The price the user would pay if they signed up monthly at full price over 12 months **/
export const GUILD_BASIC_YEARLY_FULL_PRICE = 14400

/** The standard discounted pricing for Basic Yearly **/
export const GUILD_BASIC_YEARLY_STANDARD_PRICE = 11900

/** The standard discounted pricing for Basic No Ads Yearly **/
export const GUILD_BASIC_NO_ADS_YEARLY_STANDARD_PRICE = 11900

export type GuildOfferPrice = {
  standardPrice: number
  bimOfferId: string
}

export interface GuildOfferAnnualPrice extends GuildOfferPrice {
  /** fullPrice: The price the user would pay if they signed up monthly at full price over 12 months **/
  fullPrice: number
}

export type GuildOffer = {
  tier: 'premium' | 'basic' | 'basic-no-ads'
  monthly: GuildOfferPrice
  annual: GuildOfferAnnualPrice
}

export type GuildOfferPackage = {
  premium: GuildOffer
  basic: GuildOffer
  basicNoAds?: GuildOffer
}

export const GUILD_OFFER_PACKAGES = {
  premium_20_179: {
    tier: 'premium',
    monthly: {
      standardPrice: GUILD_PREMIUM_MONTHLY_STANDARD_PRICE,
      bimOfferId: ANGEL_GUILD_PREMIUM_MONTHLY_OFFER_ID,
    },
    annual: {
      standardPrice: GUILD_PREMIUM_YEARLY_STANDARD_PRICE,
      fullPrice: GUILD_PREMIUM_YEARLY_FULL_PRICE,
      bimOfferId: ANGEL_GUILD_PREMIUM_YEARLY_OFFER_ID,
    },
  },
  premium_22_189: {
    tier: 'premium',
    monthly: {
      standardPrice: 2200,
      bimOfferId: ANGEL_GUILD_PREMIUM_MONTHLY_22_OFFER_ID,
    },
    annual: {
      standardPrice: 18900,
      fullPrice: 26400,
      bimOfferId: ANGEL_GUILD_PREMIUM_YEARLY_189_OFFER_ID,
    },
  },
  premium_22_179: {
    tier: 'premium',
    monthly: {
      standardPrice: 2200,
      bimOfferId: ANGEL_GUILD_PREMIUM_MONTHLY_22_OFFER_ID,
    },
    annual: {
      standardPrice: GUILD_PREMIUM_YEARLY_STANDARD_PRICE,
      fullPrice: 26400,
      bimOfferId: ANGEL_GUILD_PREMIUM_YEARLY_OFFER_ID,
    },
  },
  premium_25_199: {
    tier: 'premium',
    monthly: {
      standardPrice: 2500,
      bimOfferId: ANGEL_GUILD_PREMIUM_MONTHLY_25_OFFER_ID,
    },
    annual: {
      standardPrice: 19900,
      fullPrice: 30000,
      bimOfferId: ANGEL_GUILD_PREMIUM_YEARLY_199_OFFER_ID,
    },
  },
  basicWithAds: {
    tier: 'basic',
    monthly: {
      standardPrice: GUILD_BASIC_MONTHLY_STANDARD_PRICE,
      bimOfferId: ANGEL_GUILD_BASIC_MONTHLY_OFFER_ID,
    },
    annual: {
      standardPrice: GUILD_BASIC_YEARLY_STANDARD_PRICE,
      fullPrice: GUILD_BASIC_YEARLY_FULL_PRICE,
      bimOfferId: ANGEL_GUILD_BASIC_YEARLY_OFFER_ID,
    },
  },
  basicNoAds_20_179: {
    tier: 'basic-no-ads',
    monthly: {
      standardPrice: 2000,
      bimOfferId: ANGEL_GUILD_BASIC_NO_ADS_MONTHLY_20_OFFER_ID,
    },
    annual: {
      standardPrice: 17900,
      fullPrice: 24000,
      bimOfferId: ANGEL_GUILD_BASIC_NO_ADS_YEARLY_179_OFFER_ID,
    },
  },
  basicNoAds_20_169: {
    tier: 'basic-no-ads',
    monthly: {
      standardPrice: 2000,
      bimOfferId: ANGEL_GUILD_BASIC_NO_ADS_MONTHLY_20_OFFER_ID,
    },
    annual: {
      standardPrice: 16900,
      fullPrice: 24000,
      bimOfferId: ANGEL_GUILD_BASIC_NO_ADS_YEARLY_OFFER_ID,
    },
  },
  basicNoAds_18_169: {
    tier: 'basic-no-ads',
    monthly: {
      standardPrice: 1800,
      bimOfferId: ANGEL_GUILD_BASIC_NO_ADS_MONTHLY_OFFER_ID,
    },
    annual: {
      standardPrice: 16900,
      fullPrice: 21600,
      bimOfferId: ANGEL_GUILD_BASIC_NO_ADS_YEARLY_OFFER_ID,
    },
  },
} as const satisfies Record<string, GuildOffer>

type Tier = 'premium' | 'basic' | 'basic-no-ads'
type Interval = 'monthly' | 'annual'

interface PlanDetails {
  tier: Tier
  interval: Interval
}

export function findTierAndIntervalByBimOfferId(bimOfferId: string | number): PlanDetails | null {
  const target = String(bimOfferId)
  for (const plan of Object.values(GUILD_OFFER_PACKAGES)) {
    if (plan.monthly?.bimOfferId === target) {
      return { tier: plan.tier, interval: 'monthly' }
    }
    if (plan.annual?.bimOfferId === target) {
      return { tier: plan.tier, interval: 'annual' }
    }
  }
  return null
}

export const PRICE_ANCHOR_EXPERIMENT_VARIANTS = {
  OFF: {
    basic: GUILD_OFFER_PACKAGES['basicWithAds'],
    premium: GUILD_OFFER_PACKAGES['premium_20_179'],
  },
  '18-169 / 20-179': {
    basic: GUILD_OFFER_PACKAGES['basicWithAds'],
    basicNoAds: GUILD_OFFER_PACKAGES['basicNoAds_18_169'],
    premium: GUILD_OFFER_PACKAGES['premium_20_179'],
  },
  '20-169 / 22-179': {
    basic: GUILD_OFFER_PACKAGES['basicWithAds'],
    basicNoAds: GUILD_OFFER_PACKAGES['basicNoAds_20_169'],
    premium: GUILD_OFFER_PACKAGES['premium_22_179'],
  },
  '20-179 / 22-189': {
    basic: GUILD_OFFER_PACKAGES['basicWithAds'],
    basicNoAds: GUILD_OFFER_PACKAGES['basicNoAds_20_179'],
    premium: GUILD_OFFER_PACKAGES['premium_22_189'],
  },
  '20-179 / 25/199': {
    basic: GUILD_OFFER_PACKAGES['basicWithAds'],
    basicNoAds: GUILD_OFFER_PACKAGES['basicNoAds_20_179'],
    premium: GUILD_OFFER_PACKAGES['premium_25_199'],
  },
} as const satisfies Record<string, GuildOfferPackage>

export function isValidPriceAnchorExperimentVariant(
  variant: string,
): variant is keyof typeof PRICE_ANCHOR_EXPERIMENT_VARIANTS {
  return variant in PRICE_ANCHOR_EXPERIMENT_VARIANTS
}
